import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { CustomFieldType } from '@ee/common/enums';
import { LayoutItem } from '@ee/common/models';
import { COMPOSITE_PARTS, askedParts } from './composites';
import { controlNameFor, isAnswered, rentHistorySum, tenantFieldNotAsked, tenantFieldRelaxed } from './intake-form-controls';
import { RuleOutcome } from './layout-rule-evaluation';
import { FieldDefinitionView, LayoutPageView, isTenantField } from './intake-layout.model';

/**
 * The Review step, generated from the layout.
 *
 * Nothing here is authored. A hand-written summary goes stale the first time a firm adds a field, and a
 * client would then be asked to confirm a form that is not the one they filled in — which on an
 * eviction filing is the summary a court sees.
 */

/** One thing standing between the client and filing. */
export interface PreflightProblem {
  /** Storage key, for a stable list identity. */
  key: string;
  /** "Email is missing" — what, in the client's words. */
  title: string;
  /** "We send the case link there." Why it is needed, which is the part a client can act on. */
  why: string;
  /** Which step to send them to. */
  pageIndex: number;
  /** An upload is fixed by uploading, not by typing. */
  upload: boolean;
}

export interface ReviewRow {
  label: string;
  value: string;
}

export interface ReviewGroup {
  title: string;
  pageIndex: number;
  rows: ReviewRow[];
}

/** Carried address parts a filer can read back: what the block showed or resolved as the venue. */
const REVIEWED_VENUE_PARTS = COMPOSITE_PARTS.ADDRESS.filter(part => ['containing_subdivision', 'place'].includes(part.key));

/** Why a missing answer matters, where the product knows better than "this field is required". */
const WHY: Record<string, string> = {
  email: 'We send the case link there.',
  contact_email: 'We send the case link there.',
  phone_number: 'The court needs a daytime number.',
  contact_phone: 'The court needs a daytime number.',
  company_name: 'We cannot open your account without it.',
  plaintiff: 'The complaint is filed in this name.',
  property_street_address: 'The complaint names the unit being recovered.',
  property_zip_code: 'It decides which county hears the case.'
};

function labelFor(item: LayoutItem, definitions: Map<string, FieldDefinitionView>): string {
  return item.label_override || definitions.get(item.field_key ?? '')?.label || item.field_key || 'This field';
}

/**
 * One answer, as a client reads it back.
 *
 * Only scalars are stringified. An object answer — an upload, a party — has no useful text form, and
 * `String(value)` on one renders "[object Object]" into the summary a court sees.
 */
function rendered(value: unknown, definition: FieldDefinitionView | undefined): string {
  if (!isAnswered(value)) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (definition?.type === CustomFieldType.RENT_HISTORY && Array.isArray(value)) {
    // Months and their sum: the rows themselves are a table, and the summary is meant to be read.
    return `${value.length} month${value.length === 1 ? '' : 's'} · $${rentHistorySum(value).toFixed(2)}`;
  }
  if (Array.isArray(value)) {
    return value.map(one => rendered(one, undefined)).filter(Boolean).join(', ');
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    return 'Provided';
  }
  if (definition?.monetary) {
    const amount = Number(String(value).replace(/[$,]/g, ''));
    return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : String(value);
  }
  return String(value);
}

/** Placements a client is actually being asked for: not blocks, not hidden by a rule. */
function askedOn(page: LayoutPageView, rules: RuleOutcome): { section: string; item: LayoutItem }[] {
  return page.sections
    .filter(section => !rules.hidden.has(section.key))
    .flatMap(section =>
      section.rows
        .flatMap(row => row.items)
        .filter(item => item.kind === 'FIELD' || item.kind === 'COMPOSITE')
        .filter(item => !rules.hidden.has(item.field_key ?? ''))
        .map(item => ({ section: section.title, item }))
    );
}

/**
 * Everything unanswered that has to be answered.
 *
 * Required is the union of the placement's own flag and whatever the rules add, because a rule that
 * requires a field is the reason it appeared at all — and a client who has just been shown a question
 * should not be able to walk past it.
 */
export function preflight(
  pages: LayoutPageView[],
  form: FormGroup,
  definitions: Map<string, FieldDefinitionView>,
  rules: RuleOutcome
): PreflightProblem[] {
  const problems: PreflightProblem[] = [];

  pages.forEach((page, pageIndex) => {
    askedOn(page, rules).forEach(({ item }) => {
      const definition = definitions.get(item.field_key ?? '');
      const upload = definition?.type === CustomFieldType.FILE_UPLOAD;

      if (item.kind === 'COMPOSITE' && item.composite_type) {
        (COMPOSITE_PARTS[item.composite_type] ?? [])
          // A carried part is never asked, so it is listed only when the block has made it required
          // (v1's county / precinct / place `[required]`) and it is still blank.
          .filter(part => !part.derived && (!part.carried || form.get(part.key)?.hasError('required')))
          // A section-targeted rule reaches each part, so a part answers to the rules like any field.
          .filter(part => !rules.disabled.has(part.key) && !rules.hidden.has(part.key))
          .forEach(part => {
            const control = form.get(part.key);
            // OPTIONAL waives only the answer being required: a relaxed part must still be well-formed.
            const relaxed = rules.relaxed.has(part.key);
            if (!relaxed && (control?.hasError('required') || (rules.required.has(part.key) && !isAnswered(control?.value)))) {
              problems.push({
                key: part.key,
                title: `${part.label} is missing`,
                why: WHY[part.key] ?? 'The filing needs it.',
                pageIndex,
                upload: false
              });
            } else if (invalidAnswer(control)) {
              problems.push(checkAnswer(part.key, part.label, pageIndex, control, false, definitions.get(part.key)));
            }
          });
        return;
      }

      const key = item.field_key;
      if (!key) {
        return;
      }

      // A rule that disables this has the last word over the placement. One that makes it optional waives
      // only the requiredness (as `pageIsValid` does), so an answer given must still be well-formed.
      if (rules.disabled.has(key)) {
        return;
      }
      const relaxed = rules.relaxed.has(key);

      // The tenant party repeats, so one unanswered name is a problem with the party rather than with a
      // control a client could be pointed at by key. Rules target the placed key, so a rule requiring
      // `tenants.email` asks it of every tenant row.
      if (isTenantField(key)) {
        const tenants = form.get('tenants') as FormArray | null;
        const name = controlNameFor(key);
        // The same predicates Continue reads, so Review and Continue cannot drift on a tenant field.
        if (tenantFieldNotAsked(name, rules)) {
          return;
        }
        const tenantRelaxed = relaxed || tenantFieldRelaxed(name, rules);
        const anyMissing =
          !tenantRelaxed &&
          (tenants?.controls ?? []).some(
            tenant =>
              tenant.get(name)?.hasError('required') || (rules.required.has(key) && !isAnswered(tenant.get(name)?.value))
          );
        if (anyMissing && !problems.some(problem => problem.key === `tenants.${name}`)) {
          problems.push({
            key: `tenants.${name}`,
            title: `A tenant needs a ${labelFor(item, definitions).toLowerCase()}`,
            why: 'Every tenant on the lease must be named.',
            pageIndex,
            upload: false
          });
        } else if (!anyMissing && !problems.some(problem => problem.key === `tenants.${name}`)) {
          const invalid = (tenants?.controls ?? []).map(tenant => tenant.get(name)).find(control => invalidAnswer(control));
          if (invalid) {
            problems.push(
              checkAnswer(`tenants.${name}`, `A tenant's ${labelFor(item, definitions).toLowerCase()}`, pageIndex, invalid, false, definitions.get(key))
            );
          }
        }
        return;
      }

      const control = form.get(key);
      const requiredByRule = !relaxed && rules.required.has(key) && !isAnswered(control?.value);
      if (!relaxed && (control?.hasError('required') || requiredByRule)) {
        problems.push({
          key,
          title: `${labelFor(item, definitions)} is ${upload ? 'missing' : requiredByRule ? 'needed' : 'missing'}`,
          // A rule's own reason beats the generic one: the firm wrote it to explain this exact case.
          why:
            rules.reasons.get(key) ??
            WHY[key] ??
            (upload ? 'The county will not accept a filing without it.' : 'The filing needs it.'),
          pageIndex,
          upload
        });
      } else if (invalidAnswer(control)) {
        problems.push(checkAnswer(key, labelFor(item, definitions), pageIndex, control, upload, definitions.get(key)));
      }
    });
  });

  return problems;
}

/**
 * Answered, but not acceptably: a malformed email or URL, a negative amount. Continue already refuses
 * these (`pageIsValid` reads every validator), so they are listed too — a disabled button with nothing
 * beside it to explain why is the one thing this list exists to prevent.
 */
function invalidAnswer(control: AbstractControl | null | undefined): control is AbstractControl {
  if (!control || control.valid) {
    return false;
  }
  // A rent history is invalid through its rows — a month with no amount — which the array's own errors
  // never show; Continue refuses it all the same, so it is listed.
  if (control instanceof FormArray && control.controls.some(row => row.invalid)) {
    return true;
  }
  return Object.keys(control.errors ?? {}).some(error => error !== 'required');
}

/** Why a failed upload failed, in the client's words. Keyed by `uploadFailed`'s reason. */
const UPLOAD_FAILURE: Record<string, string> = {
  infected: 'The file was flagged by the virus scan. Upload a clean copy.',
  rejected: 'That type of file cannot be accepted. Upload a PDF, image or Word document.',
  failed: 'The upload did not finish. Try again.'
};

/**
 * The problem for an answer that is given but fails validation. An upload keeps its own wording and its
 * Upload action — a rejected, infected, failed or oversized file is fixed by choosing another file, not
 * by retyping a value.
 */
function checkAnswer(
  key: string,
  label: string,
  pageIndex: number,
  control: AbstractControl,
  upload: boolean,
  definition?: FieldDefinitionView
): PreflightProblem {
  if (upload || control.hasError('uploadFailed') || control.hasError('maxContentSize')) {
    const reason = control.getError('uploadFailed') as string | null;
    const why = control.hasError('maxContentSize')
      ? 'The file is too large. Upload a smaller copy.'
      : (reason && UPLOAD_FAILURE[reason]) || 'Upload the file again.';
    return { key, title: `${label} needs a new upload`, why, pageIndex, upload: true };
  }
  return { key, title: `${label} needs checking`, why: whyInvalid(control, definition), pageIndex, upload: false };
}

/** What is wrong with an answer, in the client's words — v1's inline messages, where it had them. */
function whyInvalid(control: AbstractControl, definition?: FieldDefinitionView): string {
  if (control instanceof FormArray) {
    // A rent history has its own words; any other list of rows — a party on a case template — gets
    // the generic ones, not a month nobody asked for.
    if (definition?.type === CustomFieldType.RENT_HISTORY) {
      return control.hasError('uniquePastDueRentHistory')
        ? 'The same month is listed twice.'
        : 'Each month needs a month, a year and an amount greater than 0.';
    }
    return 'One of the entries is incomplete.';
  }
  if (control.hasError('currency')) {
    return 'Enter a dollar amount with up to two decimals.';
  }
  if (control.hasError('min')) {
    return (control.getError('min') as { min?: number })?.min ? 'Must be greater than 0.' : 'Cannot be negative.';
  }
  return 'The answer is not in the expected format.';
}

/**
 * The summary, one group per page that asks anything.
 *
 * Grouped by page rather than by section: a client remembers the step they filled in, and a form with
 * eleven sections would produce eleven cards nobody reads.
 */
export function reviewSummary(
  pages: LayoutPageView[],
  form: FormGroup,
  definitions: Map<string, FieldDefinitionView>,
  rules: RuleOutcome
): ReviewGroup[] {
  const groups: ReviewGroup[] = [];

  pages.forEach((page, pageIndex) => {
    const asked = askedOn(page, rules);
    if (!asked.length) {
      return;
    }
    const rows: ReviewRow[] = [];

    asked.forEach(({ item }) => {
      const definition = definitions.get(item.field_key ?? '');

      if (item.kind === 'COMPOSITE' && item.composite_type) {
        // The block is one question, so it is one row — a client checking an address wants to read it,
        // not audit seven parts of it.
        // The asked parts, then the venue the block resolved or asked beside them — the county the case
        // is filed in, and a township and place the filer may have typed. Not the county record, the
        // coordinates or the courts: those are not something to read back.
        const parts = [...askedParts(item.composite_type), ...REVIEWED_VENUE_PARTS]
          .map(part => rendered(form.get(part.key)?.value, definitions.get(part.key)))
          .filter(Boolean);
        rows.push({ label: item.label_override || 'Address', value: [...new Set(parts)].join(' · ') });
        return;
      }

      const key = item.field_key;
      if (!key) {
        return;
      }

      if (isTenantField(key)) {
        const name = controlNameFor(key);
        // Only once per party, from the first placed tenant field: a row per tenant per field would be
        // a table, and the summary is meant to be read.
        if (rows.some(row => row.label === 'Tenants')) {
          return;
        }
        const tenants = (form.get('tenants') as FormArray | null)?.controls ?? [];
        const names = tenants
          .map(tenant =>
            [tenant.get('first_name')?.value, tenant.get('last_name')?.value ?? tenant.get(name)?.value]
              .filter(Boolean)
              .join(' ')
              .trim()
          )
          .filter(Boolean);
        rows.push({ label: 'Tenants', value: names.join(', ') });
        return;
      }

      rows.push({
        label: labelFor(item, definitions),
        value:
          definition?.type === CustomFieldType.FILE_UPLOAD
            ? isAnswered(form.get(key)?.value)
              ? 'Uploaded'
              : ''
            : rendered(form.get(key)?.value, definition)
      });
    });

    groups.push({ title: page.name, pageIndex, rows });
  });

  return groups;
}
